import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  Tooltip,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { Activity } from 'lucide-react';

const STATUS_COLORS = {
  APROBADO: '#22C55E',
  'EN APROBACION': '#F59E0B',
  DOCUMENTADO: '#0EA5E9',
  RECHAZADO: '#F43F5E',
  'SIN ESTATUS': '#A56EFF'
};

const FALLBACK_COLORS = ['#00FF66', '#1EEB8A', '#34D399', '#2DD4BF', '#00D4AA', '#A56EFF'];

const normalizeStatus = (status) => {
  const raw = String(status || '').trim();
  if (!raw) return 'SIN ESTATUS';

  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

  if (normalized.includes('RECHAZ')) return 'RECHAZADO';
  if (normalized.includes('APROBACION')) return 'EN APROBACION';
  if (normalized.includes('APROBAD')) return 'APROBADO';
  if (normalized.includes('DOCUMENT')) return 'DOCUMENTADO';

  return raw.toUpperCase();
};

const getStatusColor = (status, index = 0) => {
  return STATUS_COLORS[status] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

export default function HorasPorEstatus({
  records = [],
  isDarkMode = true,
  completePeriod = false
}) {
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  const { dataEstatus, horasPorDiaData } = useMemo(() => {
    const totalsByStatus = new Map();
    const totalsByDate = new Map();

    records.forEach((record) => {
      const status = normalizeStatus(record.estatus);
      const horas = Number(record.tiempo_empleado_hrs || 0);
      const fechaRaw = record.fecha_inicio ? new Date(record.fecha_inicio) : null;

      if (Number.isFinite(horas) && horas > 0) {
        totalsByStatus.set(status, (totalsByStatus.get(status) || 0) + horas);
      }

      if (fechaRaw && !Number.isNaN(fechaRaw.getTime()) && Number.isFinite(horas) && horas > 0) {
        const dateKey = fechaRaw.toISOString().slice(0, 10);
        if (!totalsByDate.has(dateKey)) {
          totalsByDate.set(dateKey, {});
        }
        const dayData = totalsByDate.get(dateKey);
        dayData[status] = (dayData[status] || 0) + horas;
      }
    });

    const estatusSorted = Array.from(totalsByStatus.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const statuses = estatusSorted.map((item) => item.name);

    const fechasOrdenadas = Array.from(totalsByDate.keys()).sort();
    const series = fechasOrdenadas.map((dateKey) => {
      const date = new Date(`${dateKey}T00:00:00`);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const values = totalsByDate.get(dateKey) || {};

      return {
        fecha: `${day}/${month}`,
        ...statuses.reduce((acc, status) => {
          acc[status] = Number(values[status] || 0);
          return acc;
        }, {})
      };
    });

    return {
      dataEstatus: estatusSorted,
      horasPorDiaData: series
    };
  }, [records]);

  const totalHoras = dataEstatus.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltipBar = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const value = Number(data.value || 0);
      const percent = totalHoras > 0 ? (value / totalHoras) * 100 : 0;

      return (
        <div className={`${
          isDarkMode
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40'
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <div className={`${textPrimary} font-semibold text-sm`}>{data.name}</div>
          <div className={`${textSecondary} text-sm`}>
            {value.toLocaleString('es-MX', { maximumFractionDigits: 2 })} horas
          </div>
          <div className={`${textMuted} text-xs`}>
            {percent.toFixed(1)}%
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomTooltipLine = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${
          isDarkMode
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40'
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <div className={`${textPrimary} font-semibold text-sm mb-2`}>{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
              <span className={`${textSecondary} text-xs`}>
                {entry.name}: {Number(entry.value || 0).toFixed(1)} hrs
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`${cardClasses} p-4 mt-4`}>
      <h3 className={`${isDarkMode ? 'text-[#00FF66]' : textPrimary} text-lg font-bold mb-1 flex items-center gap-2`}>
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-md border"
          style={{
            backgroundColor: isDarkMode ? 'rgba(14,165,233,0.14)' : 'rgba(14,165,233,0.1)',
            borderColor: isDarkMode ? 'rgba(14,165,233,0.35)' : 'rgba(14,165,233,0.3)'
          }}
        >
          <Activity width={15} height={15} color="#0EA5E9" />
        </span>
        Horas por Estatus
      </h3>
      <p className={`${textMuted} text-xs mb-4`}>
        {completePeriod ? 'Registros del periodo seleccionado.' : 'Basado en registros detallados cargados en el dashboard (maximo 100 por consulta).'}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 flex flex-col">
          <h4 className={`${textSecondary} text-sm font-semibold mb-3 text-center`}>
            Total por Estatus
          </h4>

          {dataEstatus.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className={`${textMuted} text-sm`}>No hay datos de estatus</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={dataEstatus}
                layout="vertical"
                margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDarkMode ? '#0e3d25' : '#E5E7EB'}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltipBar />} cursor={{ fill: 'rgba(0,255,102,0.08)' }} />
                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                  {dataEstatus.map((entry, index) => (
                    <Cell key={`status-bar-${index}`} fill={getStatusColor(entry.name, index)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="lg:col-span-2 flex flex-col">
          <h4 className={`${textSecondary} text-sm font-semibold mb-3`}>
            Evolucion por Dia
          </h4>

          {horasPorDiaData.length === 0 || dataEstatus.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className={`${textMuted} text-sm`}>No hay datos de evolucion diaria</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart
                data={horasPorDiaData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  {dataEstatus.map((estatus, index) => (
                    <linearGradient
                      key={`gradient-status-${index}`}
                      id={`colorStatus${index}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={getStatusColor(estatus.name, index)} stopOpacity={0.5} />
                      <stop offset="95%" stopColor={getStatusColor(estatus.name, index)} stopOpacity={0.05} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#0e3d25' : '#E5E7EB'} />
                <XAxis
                  dataKey="fecha"
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  style={{ fontSize: '12px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  style={{ fontSize: '12px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltipLine />} />
                <Legend
                  wrapperStyle={{
                    fontSize: '12px',
                    color: isDarkMode ? '#d9d9d9' : '#000'
                  }}
                />
                {dataEstatus.map((estatus, index) => (
                  <Area
                    key={`area-status-${index}`}
                    type="monotone"
                    dataKey={estatus.name}
                    stroke={getStatusColor(estatus.name, index)}
                    strokeWidth={2}
                    fill={`url(#colorStatus${index})`}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
